import { EMAIL_REGEXP } from 'src/utils/validation-schemes';
import { WORKFLOWS_ACTION_MESSAGES_TYPES } from 'src/pages/ConfigurationPage/WorkflowsTab/constants';
import type { WorkflowFormAction } from 'src/pages/ConfigurationPage/WorkflowsTab/workflowFormValues';

// Validates the actions of a workflow form (see WorkflowFormValues).
interface WorkflowData {
  action?: Partial<WorkflowFormAction>[];
}

type SetErrorFn = (field: string, error: { message: string }) => void;

// Forwarding to email needs a valid receiver address.
const validateReceiverEmail = (
  action: Partial<WorkflowFormAction>,
  index: number,
  setError: SetErrorFn,
): boolean => {
  if (action.action_escalated_list !== 'forward_to_email') {
    return true;
  }

  if (!action.receiver_email_addresses) {
    setError(`action.${index}.receiver_email_addresses`, {
      message: 'required',
    });

    return false;
  }

  if (!EMAIL_REGEXP.test(action.receiver_email_addresses)) {
    setError(`action.${index}.receiver_email_addresses`, {
      message: 'Invalid Format',
    });

    return false;
  }

  return true;
};

// Every custom data-collection field needs a name and a description.
const validateCustomFields = (
  action: Partial<WorkflowFormAction>,
  index: number,
  setError: SetErrorFn,
): boolean => {
  let isValid = true;

  action.action_fields_custom_array.forEach((field, indx) => {
    if (!field.name) {
      setError(`action.${index}.action_fields_custom_array.${indx}.name`, {
        message: 'required',
      });

      isValid = false;
    }

    if (!field.description) {
      setError(`action.${index}.action_fields_custom_array.${indx}.description`, {
        message: 'required',
      });

      isValid = false;
    }
  });

  return isValid;
};

export const validateWorkflowActions = (
  data: WorkflowData,
  setError: SetErrorFn = () => {},
): boolean => {
  let isValid = true;

  if (!data.action) {
    return false;
  }

  data.action.forEach((action, index) => {
    const isDataCollectionChecked = [
      action.action_fields_name,
      action.action_fields_email,
      action.action_fields_phone_number,
      action.action_fields_custom,
    ].some(Boolean);
    const isActionTypeEscalate = action.action_type === 'escalate';
    const isEscalateOrForward =
      isActionTypeEscalate &&
      ['collect_and_escalate', 'forward_to_email'].includes(action.action_escalated_list);

    if (!action.action_data_collection && isEscalateOrForward && !isDataCollectionChecked) {
      setError(`action.${index}.action_data_collection`, {
        message: 'required',
      });

      isValid = false;
    }

    if (isEscalateOrForward && !isDataCollectionChecked) {
      setError(`action.${index}.action_fields_name`, {
        message: 'required',
      });
      setError(`action.${index}.action_fields_email`, {
        message: 'required',
      });
      setError(`action.${index}.action_fields_phone_number`, {
        message: 'required',
      });
      setError(`action.${index}.action_fields_custom`, {
        message: 'required',
      });

      isValid = false;
    }

    if (!validateReceiverEmail(action, index, setError)) {
      isValid = false;
    }

    if (!action.action_escalated_list && isActionTypeEscalate) {
      setError(`action.${index}.action_escalated_list`, {
        message: 'required',
      });

      isValid = false;
    }

    if (!action.action_message && WORKFLOWS_ACTION_MESSAGES_TYPES.includes(action.action_type)) {
      setError(`action.${index}.action_message`, {
        message: 'required',
      });

      isValid = false;
    }

    if (!action.message_close && isActionTypeEscalate && action.action_escalated_list === 'close') {
      setError(`action.${index}.message_close`, {
        message: 'required',
      });

      isValid = false;
    }

    if (
      isActionTypeEscalate &&
      action.action_fields_custom &&
      !validateCustomFields(action, index, setError)
    ) {
      isValid = false;
    }
  });

  return isValid;
};
